<template>
  <div class="w-full">
    <!-- Subsidi Ongkir -->
    <b-card class="border rounded-xl border-gray-200 mb-3">
      <div class="flex items-start justify-between pb-2 mb-3 border-b border-gray-100">
        <div>
          <h5 class="text-base font-semibold text-[#333333] mb-0.5">
            Subsidi Ongkir
          </h5>
          <p class="text-xs md:text-sm text-[#828282] mb-0">
            Atur subsidi ongkir dengan lebih baik dan efisien.
          </p>
        </div>
        <b-form-checkbox
          id="toggle-subsidi"
          v-model="subsidyEnabled"
          switch
          class="custom-promo-switch mb-0"
          @change="onSubsidyToggle"
        />
      </div>

      <b-row class="gap-y-3">
        <!-- Row 1: Jenis Diskon & Nominal Diskon -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            label-for="select-subsidy-type"
            label-class="text-sm font-medium text-[#333333] mb-0.5"
          >
            <template #label>
              Jenis Diskon<span class="text-danger">*</span>
            </template>
            <v-select
              id="select-subsidy-type"
              v-model="subsidyType"
              :options="subsidyTypeOptions"
              :reduce="o => o.value"
              label="label"
              :clearable="false"
              placeholder="Persentase"
              :disabled="!subsidyEnabled"
              class="custom-promo-select"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            label-for="input-shipping-discount"
            label-class="text-sm font-medium text-[#333333] mb-0.5"
          >
            <template #label>
              Nominal Diskon<span class="text-danger">*</span>
            </template>
            <b-input-group
              :prepend="subsidyType === 'nominal' ? 'Rp' : null"
              :append="subsidyType !== 'nominal' ? '%' : null"
              class="custom-promo-group"
            >
              <b-form-input
                id="input-shipping-discount"
                v-model.number="formData.shipping_discount"
                type="number"
                min="0"
                :disabled="!subsidyEnabled"
                :max="subsidyType === 'percentage' ? 100 : undefined"
                placeholder="0"
                @input="onShippingDiscountInput"
                @keypress="onShippingDiscountKeypress"
                @paste="onShippingDiscountPaste"
              />
            </b-input-group>
          </b-form-group>
        </b-col>

        <!-- Row 2: Minimum Transaksi & Coverage -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            label-for="input-minimum-shipping-discount"
            label-class="text-sm font-medium text-[#333333] mb-0.5"
          >
            <template #label>
              Minimum Transaksi<span class="text-danger">*</span>
            </template>
            <b-input-group
              prepend="Rp"
              class="custom-promo-group"
            >
              <b-form-input
                id="input-minimum-shipping-discount"
                v-model.number="formData.minimum_shipping_discount"
                type="number"
                min="0"
                :disabled="!subsidyEnabled"
                placeholder="0"
              />
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            label-for="select-coverage"
            label-class="text-sm font-medium text-[#333333] mb-0.5"
          >
            <template #label>
              Coverage<span class="text-danger">*</span>
            </template>
            <v-select
              id="select-coverage"
              v-model="selectedCoverage"
              :options="availableCoverageOptions"
              :selectable="isCoverageSelectable"
              placeholder="Seluruh Indonesia"
              :disabled="!subsidyEnabled"
              class="custom-promo-select"
              @input="addCoverage"
            />
            <div
              v-if="selectedCoverages.length > 0"
              class="flex flex-wrap gap-1 mt-1"
            >
              <span
                v-for="coverage in selectedCoverages"
                :key="coverage"
                class="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700 border border-gray-200"
              >
                {{ getCoverageLabel(coverage) }}
                <button
                  type="button"
                  class="ml-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                  @click="removeCoverage(coverage)"
                >
                  ×
                </button>
              </span>
            </div>
          </b-form-group>
        </b-col>

        <!-- Row 3: Diskon Pembayaran (Full width) -->
        <b-col cols="12">
          <b-form-group
            label-for="input-payment-discount"
            label-class="text-sm font-medium text-[#333333] mb-0.5"
          >
            <template #label>
              Diskon Pembayaran
            </template>
            <b-form-input
              id="input-payment-discount"
              v-model.number="formData.payment_discount"
              type="text"
              :disabled="!subsidyEnabled"
              placeholder="e.g. 50%"
              class="custom-promo-input"
              @input="onPaymentDiscountInput"
              @keypress="onPaymentDiscountKeypress"
              @paste="onPaymentDiscountPaste"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import VSelect from 'vue-select'

export default {
  components: { VSelect },
  props: {
    formData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      subsidyEnabled: false,
      selectedCoverage: null,
      selectedCoverages: [],
      subsidyTypeOptions: [
        { label: 'Persentase', value: 'percentage' },
        { label: 'Nominal', value: 'nominal' },
      ],
      coverageOptions: [
        { label: 'Seluruh Indonesia', value: 'indonesia' },
        { label: 'Pulau Jawa', value: 'java_island' },
        { label: 'Luar Pulau Jawa', value: 'outside_java' },
        { label: 'Jabodetabek', value: 'jabodetabek' },
      ],
    }
  },
  computed: {
    subsidyType: {
      get() {
        return this.formData.type_shipping_discount || 'percentage'
      },
      set(val) {
        this.formData.type_shipping_discount = val
        if (val === 'percentage' && this.formData.shipping_discount > 100) {
          this.formData.shipping_discount = 100
        }
      },
    },
    availableCoverageOptions() {
      return this.coverageOptions.filter(o => !this.selectedCoverages.includes(o.value))
    },
  },
  watch: {
    formData: {
      immediate: true,
      deep: true,
      handler(val) {
        if (!val) return
        const hasDiscount = val.shipping_discount > 0
          || val.minimum_shipping_discount > 0
          || val.payment_discount > 0
        if (hasDiscount) {
          this.subsidyEnabled = true
        }
      },
    },
    'formData.coverage_discount': {
      immediate: true,
      handler(newVal) {
        if (typeof newVal === 'string' && newVal.trim() !== '') {
          this.selectedCoverages = [newVal]
        } else if (Array.isArray(newVal)) {
          this.selectedCoverages = [...newVal]
        } else {
          this.selectedCoverages = []
        }
      },
    },
  },
  methods: {
    onSubsidyToggle(enabled) {
      this.subsidyEnabled = enabled
      if (!enabled) {
        this.formData.shipping_discount = 0
        this.formData.minimum_shipping_discount = 0
        this.formData.coverage_discount = []
        this.formData.payment_discount = 0
        this.selectedCoverages = []
        this.formData.type_shipping_discount = null
      }
      this.$emit('validate')
    },
    getCoverageLabel(value) {
      return (this.coverageOptions.find(o => o.value === value) || {}).label || value
    },
    isCoverageSelectable(option) {
      if (!option) return true
      if (this.selectedCoverages.includes('indonesia')) return option.value === 'indonesia'
      if (option.value === 'indonesia') return this.selectedCoverages.length === 0
      return true
    },
    addCoverage(selected) {
      if (!selected) { this.selectedCoverage = null; return }
      const { value } = selected
      if (value === 'indonesia') {
        this.selectedCoverages = ['indonesia']
      } else if (!this.selectedCoverages.includes('indonesia') && !this.selectedCoverages.includes(value)) {
        this.selectedCoverages.push(value)
      }
      this.formData.coverage_discount = [...this.selectedCoverages]
      this.selectedCoverage = null
    },
    removeCoverage(value) {
      this.selectedCoverages = this.selectedCoverages.filter(c => c !== value)
      this.formData.coverage_discount = [...this.selectedCoverages]
    },
    // Discount validation helpers
    validateDiscountKeypress(event, field, isPercentage = true) {
      const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter']
      if (!/[0-9]/.test(event.key) && !allowed.includes(event.key)) {
        event.preventDefault()
        return
      }
      if (isPercentage) {
        const newVal = parseFloat((this.formData[field] || '') + event.key)
        if (!Number.isNaN(newVal) && newVal > 100) {
          event.preventDefault()
          this.formData[field] = 100
        }
      }
    },
    validateDiscountInput(event, field, isPercentage = true) {
      const raw = event && event.target ? event.target.value : event
      if (raw === '' || raw === null || raw === undefined) { this.formData[field] = 0; return }
      const num = parseFloat(raw)
      if (Number.isNaN(num)) { this.formData[field] = 0; return }

      if (isPercentage) {
        this.formData[field] = Math.min(100, Math.max(0, num))
      } else {
        this.formData[field] = Math.max(0, num)
      }
    },
    validateDiscountPaste(event, field, isPercentage = true) {
      event.preventDefault()
      const num = parseFloat(event.clipboardData.getData('text'))

      if (Number.isNaN(num)) {
        this.formData[field] = 0
      } else if (isPercentage) {
        this.formData[field] = Math.min(100, Math.max(0, num))
      } else {
        this.formData[field] = Math.max(0, num)
      }
    },
    onShippingDiscountKeypress(e) { this.validateDiscountKeypress(e, 'shipping_discount', this.subsidyType === 'percentage') },
    onShippingDiscountInput(e) { this.validateDiscountInput(e, 'shipping_discount', this.subsidyType === 'percentage') },
    onShippingDiscountPaste(e) { this.validateDiscountPaste(e, 'shipping_discount', this.subsidyType === 'percentage') },
    onPaymentDiscountKeypress(e) { this.validateDiscountKeypress(e, 'payment_discount', true) },
    onPaymentDiscountInput(e) { this.validateDiscountInput(e, 'payment_discount', true) },
    onPaymentDiscountPaste(e) { this.validateDiscountPaste(e, 'payment_discount', true) },
  },
}
</script>

<style lang="scss">
@import '~@core/scss/vue/libs/vue-select.scss';
</style>
